import {KeyboardEvent, MouseEvent, ReactElement, useCallback, useRef} from "react";
import './LowMedHigh.less';
import {Tooltip} from "@mui/material";

const VALUES = ['L', 'M', 'H'] as const;

/**
 * Functions as a three-button switch. Mainly used for Low/Medium/High, but also for
 * Account/Trust/Combined. Use props to override the colors, text, etc.
 *
 * Implements WAI-ARIA radio group pattern: one tab stop with arrow keys to cycle values.
 */
function LowMedHigh(props: LowMedHighProps) {
	const {
		defaultValue,
		onChange,
		labelValues = ['Low', 'Medium', 'High'],
		labelSizes = ['fit-content', 'fit-content', 'fit-content'],
		labelTooltips = ['','','']
	} = props;

	const buttonsRef = useRef<(HTMLButtonElement | null)[]>([]);

	const selectedIndex = VALUES.indexOf(defaultValue as typeof VALUES[number]);

	const handleClick = (e: MouseEvent, v: string) => {
		e.preventDefault();
		onChange(e, v);
	};

	const handleKeyDown = useCallback((e: KeyboardEvent) => {
		const currentIdx = selectedIndex >= 0 ? selectedIndex : 0;
		let nextIdx: number;

		if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
			e.preventDefault();
			nextIdx = (currentIdx + 1) % VALUES.length;
		} else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
			e.preventDefault();
			nextIdx = (currentIdx - 1 + VALUES.length) % VALUES.length;
		} else {
			return;
		}

		buttonsRef.current[nextIdx]?.focus();
		onChange(e as any, VALUES[nextIdx]);
	}, [selectedIndex, onChange]);

	const low = defaultValue === 'L' ? 'low-med-high__low--selected' : '';
	const med = defaultValue === 'M' ? 'low-med-high__med--selected' : '';
	const high = defaultValue === 'H' ? 'low-med-high__high--selected' : '';

	// Roving tabIndex: only selected (or first) button is tabbable
	const tabbableIndex = selectedIndex >= 0 ? selectedIndex : 0;

	return (
		<div className='low-med-high' role="radiogroup" onKeyDown={handleKeyDown}>
			<Tooltip title={labelTooltips[0]}>
				<button type="button"
						ref={el => { buttonsRef.current[0] = el; }}
						role="radio"
						aria-checked={defaultValue === 'L'}
						tabIndex={tabbableIndex === 0 ? 0 : -1}
						className={`low-med-high__low ${low}`}
						onClick={(e: MouseEvent) => handleClick(e, 'L')}
						style={{width: labelSizes[0]}}
				>
					{labelValues[0]}
				</button>
			</Tooltip>

			<Tooltip title={labelTooltips[1]}>
				<button type="button"
						ref={el => { buttonsRef.current[1] = el; }}
						role="radio"
						aria-checked={defaultValue === 'M'}
						tabIndex={tabbableIndex === 1 ? 0 : -1}
						className={`low-med-high__med ${med}`}
						onClick={(e) => handleClick(e, 'M')}
						style={{width: labelSizes[1]}}
				>
					{labelValues[1]}
				</button>
			</Tooltip>

			<Tooltip title={labelTooltips[2]}>
				<button type="button"
						ref={el => { buttonsRef.current[2] = el; }}
						role="radio"
						aria-checked={defaultValue === 'H'}
						tabIndex={tabbableIndex === 2 ? 0 : -1}
						className={`low-med-high__high ${high}`}
						onClick={(e) => handleClick(e, 'H')}
						style={{width: labelSizes[2]}}
				>
					{labelValues[2]}
				</button>
			</Tooltip>
		</div>
	)
}

export type LowMedHighProps = {
	defaultValue: string,
	onChange: (e: MouseEvent, v: string) => void,
    labelValues?: string[],
    labelSizes?: string[],
    labelTooltips?: (ReactElement | string)[],
}

export default LowMedHigh;
